import plur from 'plur';
import { formatDateTime } from '@customerio/pluma-components/react/utils/formatted-date-time';
import type { ConditionFilter } from '../types';

const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 3600;
const SECONDS_PER_DAY = 86400;

const OPERATOR_LABELS: Record<string, string> = {
  eq: 'is',
  '=': 'is',
  equals: 'is',
  '!eq': 'is not',
  '!=': 'is not',
  not_equals: 'is not',
  gt: 'is greater than',
  '>': 'is greater than',
  greater_than: 'is greater than',
  lt: 'is less than',
  '<': 'is less than',
  less_than: 'is less than',
  bw: 'is between',
  between: 'is between',
  exists: 'exists',
  '!exists': 'does not exist',
  not_exists: 'does not exist',
  contains: 'contains',
  '!contains': 'does not contain',
  not_contains: 'does not contain',
  timestamp: 'is a timestamp',
  '!timestamp': 'is not a timestamp',
  not_timestamp: 'is not a timestamp',
  timestamp_gt: 'is after',
  timestamp_lt: 'is before',
  timestamp_gta: 'is after',
  timestamp_lta: 'is before',
  timestamp_bw: 'is between',
  after_relative_timestamp: 'is after',
  before_relative_timestamp: 'is before',
  after_absolute_timestamp: 'is after',
  before_absolute_timestamp: 'is before',
  between_absolute_timestamps: 'is between',
  email_valid: 'is a valid email',
  '!email_valid': 'is not a valid email',
  not_email_valid: 'is not a valid email',
  contained_in_array: 'is contained in',
  '!contained_in_array': 'is not contained in',
};

const INVERSE_OPERATOR_LABELS: Record<string, string> = {
  eq: 'is not',
  '=': 'is not',
  gt: 'is not greater than',
  '>': 'is not greater than',
  lt: 'is not less than',
  '<': 'is not less than',
  bw: 'is not between',
  exists: 'does not exist',
  contains: 'does not contain',
  timestamp: 'is not a timestamp',
  timestamp_gt: 'is not after',
  timestamp_lt: 'is not before',
  timestamp_gta: 'is not after',
  timestamp_lta: 'is not before',
  timestamp_bw: 'is not between',
  email_valid: 'is not a valid email',
  contained_in_array: 'is not contained in',
};

export function getOperatorLabel(operator: string, inverse?: boolean): string {
  if (inverse === true && operator in INVERSE_OPERATOR_LABELS) {
    return INVERSE_OPERATOR_LABELS[operator];
  }
  return OPERATOR_LABELS[operator] ?? operator;
}

export function formatRelativeTime(seconds: number): string {
  const isPast = seconds < 0;
  const absolute = Math.abs(seconds);
  const days = Math.floor(absolute / SECONDS_PER_DAY);

  if (days === 0) {
    if (absolute < SECONDS_PER_MINUTE) return 'today';
    if (absolute < SECONDS_PER_HOUR) {
      const minutes = Math.floor(absolute / SECONDS_PER_MINUTE);
      return `${minutes} ${plur('minute', minutes)} ${isPast ? 'ago' : 'from now'}`;
    }
    const hours = Math.floor(absolute / SECONDS_PER_HOUR);
    return `${hours} ${plur('hour', hours)} ${isPast ? 'ago' : 'from now'}`;
  }

  if (days === 1) return isPast ? 'yesterday' : 'tomorrow';
  return `${days} ${plur('day', days)} ${isPast ? 'ago' : 'from now'}`;
}

export function formatAbsoluteTime(timestamp: number): string {
  try {
    return formatDateTime(timestamp, 'medium');
  } catch {
    return String(timestamp);
  }
}

function isRelativeTimestampOp(operator: string): boolean {
  return (
    operator === 'timestamp_gt' ||
    operator === 'timestamp_lt' ||
    operator === 'after_relative_timestamp' ||
    operator === 'before_relative_timestamp'
  );
}

function isAbsoluteTimestampOp(operator: string): boolean {
  return (
    operator === 'timestamp_gta' ||
    operator === 'timestamp_lta' ||
    operator === 'timestamp_bw' ||
    operator === 'after_absolute_timestamp' ||
    operator === 'before_absolute_timestamp' ||
    operator === 'between_absolute_timestamps'
  );
}

export function formatValue(value: unknown, operator?: string): string {
  if (value == null) return '';
  const asNumber = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : null;
  if (operator != null && asNumber != null && !Number.isNaN(asNumber)) {
    if (isRelativeTimestampOp(operator)) return formatRelativeTime(asNumber);
    if (isAbsoluteTimestampOp(operator)) return formatAbsoluteTime(asNumber);
  }
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.join(', ');
  return JSON.stringify(value);
}

export function formatWithin(seconds: number): string {
  if (seconds === 0) return 'ever';
  const absolute = Math.abs(seconds);
  const days = Math.floor(absolute / SECONDS_PER_DAY);
  if (days === 0) {
    if (absolute < SECONDS_PER_MINUTE) {
      return `the past ${absolute} ${plur('second', absolute)}`;
    }
    if (absolute < SECONDS_PER_HOUR) {
      const minutes = Math.floor(absolute / SECONDS_PER_MINUTE);
      return `the past ${minutes} ${plur('minute', minutes)}`;
    }
    const hours = Math.floor(absolute / SECONDS_PER_HOUR);
    return `the past ${hours} ${plur('hour', hours)}`;
  }
  return `the past ${days} ${plur('day', days)}`;
}

/**
 * Extract filters from various backend structures into a flat array + operator.
 */
export function extractFilters(raw: unknown): {
  filters: ConditionFilter[];
  operator: 'and' | 'or';
} | null {
  if (raw == null) return null;

  const parseOne = (filter: Record<string, unknown>): ConditionFilter | null => {
    const field = toString(filter.field ?? filter.attribute ?? filter.name);
    const op = toString(filter.operator);
    const inverse = filter.inverse === true;
    if (field == null) return null;
    const label = op != null ? getOperatorLabel(op, inverse) : 'is';
    return {
      field,
      operator: label,
      value: filter.value != null ? formatValue(filter.value, op) : undefined,
      value2: filter.value2 != null ? formatValue(filter.value2, op) : undefined,
    };
  };

  const parseArray = (items: unknown[]): ConditionFilter[] =>
    items
      .filter(
        (item): item is Record<string, unknown> => item != null && typeof item === 'object' && !Array.isArray(item),
      )
      .map(parseOne)
      .filter((filter): filter is ConditionFilter => filter != null);

  if (Array.isArray(raw)) return { filters: parseArray(raw), operator: 'and' };

  if (typeof raw === 'object') {
    const object = raw;
    if ('and' in object && Array.isArray(object.and)) {
      return { filters: parseArray(object.and), operator: 'and' };
    }
    if ('or' in object && Array.isArray(object.or)) {
      return { filters: parseArray(object.or), operator: 'or' };
    }
    if ('field' in object || 'attribute' in object || 'operator' in object) {
      const single = parseOne(object);
      return single != null ? { filters: [single], operator: 'and' } : null;
    }
  }
  return null;
}

/** Read a string or number field, returning string or undefined. */
export function toString(value: unknown): string | undefined {
  if (typeof value === 'string' && value !== '') return value;
  if (typeof value === 'number') return String(value);
  return undefined;
}

/** Read a number field. */
export function toNumber(value: unknown): number | undefined {
  if (typeof value === 'number') return value;
  if (typeof value === 'string' && value !== '') {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? undefined : parsed;
  }
  return undefined;
}
